import React, { useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  ListChecks,
  MessageSquareQuote,
  Users,
  Wrench,
} from 'lucide-react';

/**
 * راهنمای اپراتور پرتال ۶C.
 *
 * مخاطب این صفحه کسی است که روزانه با پنل کار می‌کند و باید بتواند به
 * شرکت‌کننده‌ها هم توضیح بدهد — پس هر بخش علاوه بر «چطور»، «چرا» را هم دارد،
 * و بخش پایانی متن آمادهٔ پاسخ به پرسش‌های رایج است.
 */

const Card: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-start gap-3 p-4 text-right"
      >
        <span className="mt-0.5 shrink-0 text-blue-500">{icon}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold text-gray-900">{title}</span>
          {subtitle ? (
            <span className="mt-0.5 block text-xs text-gray-500">{subtitle}</span>
          ) : null}
        </span>
        {open ? (
          <ChevronUp className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
        ) : (
          <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
        )}
      </button>
      {open ? (
        <div className="space-y-3 border-t border-gray-100 p-4 text-sm leading-7 text-gray-700">
          {children}
        </div>
      ) : null}
    </div>
  );
};

/** جعبهٔ نکته — برای چیزهایی که اپراتور معمولاً اشتباه می‌کند */
const Note: React.FC<{ tone?: 'info' | 'warn' | 'ok'; children: React.ReactNode }> = ({
  tone = 'info',
  children,
}) => {
  const styles = {
    info: 'border-blue-200 bg-blue-50/60 text-blue-900',
    warn: 'border-amber-200 bg-amber-50/60 text-amber-900',
    ok: 'border-emerald-200 bg-emerald-50/60 text-emerald-900',
  }[tone];
  return (
    <div className={'rounded-lg border px-3 py-2 text-[13px] leading-6 ' + styles}>{children}</div>
  );
};

/** متن آماده برای کپی — چیزی که اپراتور مستقیم به کاربر می‌گوید */
const Script: React.FC<{ children: string }> = ({ children }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
    <p className="mb-1 flex items-center gap-1 text-[11px] font-bold text-gray-500">
      <MessageSquareQuote className="h-3 w-3" />
      متن پیشنهادی برای گفتن به کاربر
    </p>
    <p className="text-[13px] leading-7 text-gray-800">{children}</p>
  </div>
);

const Step: React.FC<{ n: number; title: string; children?: React.ReactNode }> = ({
  n,
  title,
  children,
}) => (
  <div className="flex gap-3">
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-black text-blue-700">
      {n.toLocaleString('fa-IR')}
    </span>
    <div className="min-w-0 flex-1">
      <p className="font-bold text-gray-900">{title}</p>
      {children ? <div className="mt-1 text-[13px] leading-7 text-gray-600">{children}</div> : null}
    </div>
  </div>
);

const Term: React.FC<{ word: string; children: React.ReactNode }> = ({ word, children }) => (
  <div className="border-r-2 border-gray-200 pr-3">
    <p className="font-bold text-gray-900">{word}</p>
    <p className="text-[13px] leading-7 text-gray-600">{children}</p>
  </div>
);

export const PortalGuideTab: React.FC = () => (
  <div className="space-y-3">
    <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
      <p className="text-sm font-bold text-blue-900">راهنمای کار با پرتال رشد ۶C</p>
      <p className="mt-1 text-[13px] leading-7 text-blue-800">
        این صفحه برای کسی نوشته شده که روزانه با این پنل کار می‌کند. هر بخش را باز کنید تا
        هم روال کار را ببینید و هم توضیحی که می‌توانید به شرکت‌کننده‌ها بدهید.
      </p>
    </div>

    {/* ── مفاهیم ─────────────────────────────────────────────────────────── */}
    <Card
      icon={<BookOpen className="h-4 w-4" />}
      title="۱. مفاهیم پایه"
      subtitle="اگر این هفت کلمه را بدانید، بقیهٔ پنل ساده می‌شود"
      defaultOpen
    >
      <div className="space-y-3">
        <Term word="دوره (Program)">
          کل برنامهٔ کوچینگ. در حال حاضر یک دوره فعال است و همهٔ تب‌ها به همان اشاره
          می‌کنند.
        </Term>
        <Term word="محور ۶C (Track)">
          شش مسیر موضوعی دوره. هر تسک می‌تواند به یکی از آن‌ها تعلق داشته باشد تا معلوم شود
          روی کدام بُعد کسب‌وکار کار می‌شود. اختیاری است.
        </Term>
        <Term word="جلسه (Session)">
          جلسات برگزاری دوره. تسک می‌تواند به یک جلسه وصل شود تا شرکت‌کننده بفهمد این تمرین
          مربوط به کدام جلسه است.
        </Term>
        <Term word="گروه (Cohort)">
          هر گروه یک منتور دارد و حدود ۱۲ شرکت‌کننده. منتور فقط شرکت‌کنندگان گروه خودش را
          می‌بیند.
        </Term>
        <Term word="شرکت‌کننده (Participant)">
          کاربری که در دوره ثبت‌نام شده. وضعیت سلامتش (سبز، زرد، قرمز) و درصد پیشرفتش خودکار
          محاسبه می‌شود.
        </Term>
        <Term word="تسک (Task)">
          تمرینی که به شرکت‌کننده داده می‌شود. هر تسک از یک یا چند «فیلد پاسخ» ساخته می‌شود.
        </Term>
        <Term word="شاخص (Metric)">
          عددی که در طول دوره دنبال می‌شود — مثل فروش هفتگی. بخش ۴ کامل توضیحش می‌دهد.
        </Term>
      </div>
    </Card>

    {/* ── گردش کار تسک ───────────────────────────────────────────────────── */}
    <Card
      icon={<ListChecks className="h-4 w-4" />}
      title="۲. چرخهٔ عمر یک تسک"
      subtitle="از ساخت تا تأیید منتور"
    >
      <div className="space-y-3">
        <Step n={1} title="ساخت تسک در تب «تسک‌ها»">
          عنوان، توضیح، مهلت و فیلدهای پاسخ را تعیین می‌کنید. تسک تازه‌ساخته
          <strong> منتشر نشده</strong> است و کسی آن را نمی‌بیند.
        </Step>
        <Step n={2} title="انتشار و تخصیص">
          با انتشار، مشخص می‌کنید تسک برای همهٔ دوره است، یک گروه خاص، یا افراد انتخاب‌شده.
          در همین لحظه برای هر نفر یک «تخصیص» ساخته می‌شود.
        </Step>
        <Step n={3} title="پاسخ شرکت‌کننده">
          فرم را پر می‌کند و می‌فرستد. وضعیتش می‌شود «ارسال‌شده».
        </Step>
        <Step n={4} title="بازبینی منتور">
          منتور تأیید می‌کند، نمره می‌دهد، یا «نیازمند اصلاح» می‌زند.
        </Step>
        <Step n={5} title="اصلاح و ارسال مجدد">
          اگر تسک اجازهٔ ارسال مجدد داشته باشد، شرکت‌کننده دوباره می‌فرستد و
          <strong> نسخهٔ جدیدی</strong> ساخته می‌شود.
        </Step>

        <Note tone="info">
          <strong>چرا نسخه‌بندی مهم است:</strong> پاسخ قبلی و بازخورد منتور هیچ‌وقت پاک
          نمی‌شود. تاریخچهٔ اصلاح خودش بخشی از کوچینگ است و بعداً می‌شود دید فرد چطور پیشرفت
          کرده.
        </Note>

        <div>
          <p className="mb-1 font-bold text-gray-900">وضعیت‌ها</p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-right text-[13px]">
              <tbody>
                {[
                  ['شروع‌نشده', 'تخصیص داده شده ولی هنوز باز نشده'],
                  ['در حال انجام', 'شروع کرده ولی نفرستاده'],
                  ['ارسال‌شده', 'فرستاده، منتظر منتور'],
                  ['در حال بررسی', 'منتور بازش کرده'],
                  ['نیازمند اصلاح', 'منتور برگردانده'],
                  ['تأییدشده', 'منتور قبول کرده'],
                  ['عقب‌افتاده', 'مهلت گذشته و ارسال نشده — خودکار'],
                ].map(([s, d]) => (
                  <tr key={s} className="border-t border-gray-100">
                    <td className="w-32 py-1.5 font-bold text-gray-800">{s}</td>
                    <td className="py-1.5 text-gray-600">{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Card>

    {/* ── فرمت پاسخ ──────────────────────────────────────────────────────── */}
    <Card
      icon={<Users className="h-4 w-4" />}
      title="۳. فیلدهای پاسخ"
      subtitle="۱۷ فرمت — و اینکه کدام را کجا استفاده کنید"
    >
      <p>
        هر تسک فهرستی از فیلدهاست. «تسک ترکیبی» چیز جداگانه‌ای نیست: هر تسکی که بیش از یک
        فیلد داشته باشد ترکیبی است.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {[
          ['متنی (کوتاه و بلند)', 'گزارش، تحلیل، شرح تجربه'],
          ['عددی', 'فروش، تعداد مشتری، هزینه'],
          ['طیف امتیازدهی', 'میزان رضایت یا اطمینان از ۱ تا ۱۰'],
          ['تاریخ', 'زمان‌بندی اقدام'],
          ['فایل / تصویر / ویدئو / صوت', 'مدرک انجام کار'],
          ['لینک', 'آدرس سایت یا صفحهٔ اجتماعی'],
          ['تک‌گزینه‌ای و چندگزینه‌ای', 'انتخاب از فهرست مشخص'],
          ['چک‌لیست', 'تیک‌زدن فعالیت‌های انجام‌شده'],
          ['جدول', 'داده‌های چندستونی مثل فهرست محصولات'],
          ['تأیید انجام', 'فقط یک تیک تأیید'],
          ['بازخورد به فرد دیگر', 'نظر دربارهٔ هم‌گروهی'],
          ['دانلود قالب و بارگذاری', 'فایل آماده را پر و برگردانده می‌شود'],
        ].map(([k, v]) => (
          <div key={k} className="rounded-lg border border-gray-200 px-3 py-2">
            <p className="text-[13px] font-bold text-gray-800">{k}</p>
            <p className="text-xs text-gray-500">{v}</p>
          </div>
        ))}
      </div>
      <Note tone="info">
        <strong>آزمون چندگزینه‌ای می‌خواهید؟</strong> چند فیلد «انتخاب یک گزینه» پشت‌سرهم
        بگذارید. مزیتش این است که منتور می‌تواند روی تک‌تک سؤال‌ها بازخورد بدهد.
      </Note>
    </Card>

    {/* ── شاخص ───────────────────────────────────────────────────────────── */}
    <Card
      icon={<BarChart3 className="h-4 w-4" />}
      title="۴. شاخص چیست و چرا لازم است"
      subtitle="مهم‌ترین بخش این راهنما"
      defaultOpen
    >
      <p>
        فرض کنید هر هفته یک تسک می‌سازید به نام «اعداد این هفته» که فیلدی دارد به نام «فروش
        این هفته». از نظر سیستم، فیلد هفتهٔ اول و فیلد هفتهٔ دوم <strong>دو چیز کاملاً
        جدا</strong> هستند — چون هر فیلد متعلق به تسک خودش است.
      </p>
      <p>
        یعنی سیستم نمی‌داند این دو، یک چیز را می‌سنجند. <strong>شاخص همان حلقهٔ گمشده
        است:</strong> برچسبی که به فیلدهای هم‌معنی می‌چسبانید تا سیستم بفهمد این‌ها یک سری
        زمانی‌اند و بتواند نمودار روند بکشد.
      </p>

      <Note tone="warn">
        بدون وصل‌کردن فیلد به شاخص، عدد فقط <strong>ثبت</strong> می‌شود و هیچ‌وقت روی نمودار
        نمی‌آید. این رایج‌ترین دلیل «چرا نمودارم خالی است».
      </Note>

      <div className="space-y-3">
        <p className="font-bold text-gray-900">ساخت شاخص — گام‌به‌گام</p>
        <Step n={1} title="تب «تسک‌ها» را باز کنید">
          بالای صفحه پنل «شاخص‌های قابل‌پیگیری» را می‌بینید.
        </Step>
        <Step n={2} title="تسک را ویرایش کنید و فیلد عددی را باز کنید">
          روی فیلدهای «پاسخ عددی» و «طیف امتیازدهی» کادری هست: «این فیلد کدام شاخص را
          می‌سنجد؟»
        </Step>
        <Step n={3} title="«+ شاخص جدید» یا انتخاب از فهرست">
          بار اول شاخص را بسازید؛ هفته‌های بعد فقط از فهرست انتخابش کنید.
        </Step>
        <Step n={4} title="تنظیم واحد و جهت در پنل شاخص‌ها">
          واحد (تومان، نفر)، جهت، و نحوهٔ نمایش را آنجا تعیین کنید.
        </Step>
      </div>

      <div>
        <p className="mb-1 font-bold text-gray-900">جهت شاخص — تنظیم مهمی که فراموش می‌شود</p>
        <div className="space-y-1.5">
          <p>
            <strong>بیشتر بهتر است:</strong> فروش، تعداد مشتری، رضایت. افزایش سبز نشان داده
            می‌شود.
          </p>
          <p>
            <strong>کمتر بهتر است:</strong> هزینه، زمان تلف‌شده، نرخ ریزش.{' '}
            <strong>کاهش سبز</strong> نشان داده می‌شود.
          </p>
          <p>
            <strong>فقط ثبت شود:</strong> عددی که قضاوت خوب و بد ندارد.
          </p>
        </div>
        <Note tone="warn">
          اگر برای «هزینه» جهت را روی «بیشتر بهتر است» بگذارید، کاهش هزینه قرمز نشان داده
          می‌شود و گزارش گمراه‌کننده خواهد بود.
        </Note>
      </div>

      <Note tone="ok">
        <strong>نکتهٔ کاربردی:</strong> اگر تسک‌های قدیمی از قبل پاسخ عددی دارند، همین حالا
        فیلدهایشان را به یک شاخص وصل کنید — <strong>تاریخچهٔ گذشته بلافاصله روی نمودار
        ظاهر می‌شود</strong> و نیازی به جمع‌آوری دوبارهٔ داده نیست.
      </Note>
    </Card>

    {/* ── خواندن تب تحلیل ────────────────────────────────────────────────── */}
    <Card
      icon={<BarChart3 className="h-4 w-4" />}
      title="۵. خواندن تب «تحلیل»"
      subtitle="هر عددی که آنجا می‌بینید یعنی چه"
    >
      <div className="space-y-3">
        <div>
          <p className="font-bold text-gray-900">کارت‌های بالای صفحه</p>
          <p className="text-[13px] leading-7 text-gray-600">
            شاخص‌هایی که «سنجاق» کرده‌اید. عدد بزرگ، میانگین آخرین نقطه است و درصد زیرش
            مقایسه با نقطهٔ قبلی. با کلیک روی هر کارت، نمودار همان شاخص باز می‌شود.
          </p>
        </div>
        <div>
          <p className="font-bold text-gray-900">محور افقی نمودار = تسک، نه هفته</p>
          <p className="text-[13px] leading-7 text-gray-600">
            هر تسکی که فیلد وصل‌شده به این شاخص دارد، یک نقطه است؛ به ترتیب مهلت. تسک بدون
            مهلت به انتها می‌رود. اگر هفته‌ای تسک نساخته باشید، آن هفته اصلاً نقطه‌ای ندارد —
            بازهٔ خالی ساختگی نمایش داده نمی‌شود.
          </p>
        </div>
        <div>
          <p className="font-bold text-gray-900">میانگین / مجموع / میانه</p>
          <p className="text-[13px] leading-7 text-gray-600">
            <strong>میانگین</strong> برای «حال عمومی گروه». <strong>مجموع</strong> برای
            «کل فروش گروه». <strong>میانه</strong> وقتی یک نفر عدد خیلی بزرگی دارد و
            میانگین را به‌هم می‌ریزد.
          </p>
        </div>
        <div>
          <p className="font-bold text-gray-900">«فقط تأییدشده‌ها»</p>
          <p className="text-[13px] leading-7 text-gray-600">
            روشن که باشد فقط پاسخ‌هایی که منتور تأیید کرده شمرده می‌شوند. برای گزارش رسمی
            روشنش کنید؛ برای دیدن وضعیت لحظه‌ای خاموش.
          </p>
        </div>
        <div>
          <p className="font-bold text-gray-900">ستون «تغییر از ابتدا»</p>
          <p className="text-[13px] leading-7 text-gray-600">
            مقایسهٔ اولین عدد ثبت‌شدهٔ آن فرد با آخرینش — یعنی «چقدر رشد کرده»، نه اینکه
            عددش بزرگ است یا کوچک. کسی که از ۵ به ۱۰ رسیده رشد بهتری داشته تا کسی که از
            ۱۰۰ به ۱۱۰.
          </p>
        </div>
        <div>
          <p className="font-bold text-gray-900">ستون «تعداد ثبت»</p>
          <p className="text-[13px] leading-7 text-gray-600">
            مثلاً «۵ از ۶» یعنی یک هفته را نفرستاده. این ستون برای پیگیری بسیار مفید است.
          </p>
        </div>
        <Note tone="info">
          فقط <strong>آخرین نسخهٔ</strong> هر ارسال روی نمودار می‌نشیند. اگر کسی سه بار
          اصلاح کرده، فقط عدد نهایی‌اش شمرده می‌شود، نه هر سه.
        </Note>
      </div>
    </Card>

    {/* ── پرسش‌های کاربران ───────────────────────────────────────────────── */}
    <Card
      icon={<HelpCircle className="h-4 w-4" />}
      title="۶. پرسش‌های رایج کاربران"
      subtitle="با متن آمادهٔ پاسخ"
    >
      <div className="space-y-4">
        <div>
          <p className="font-bold text-gray-900">«چرا باید هر هفته عدد ثبت کنم؟»</p>
          <Script>
            چون تنها راه دیدن مسیر رشد شما همین است. یک عدد در یک هفته چیزی نشان نمی‌دهد،
            ولی شش هفته کنار هم نشان می‌دهد کدام کارتان جواب داده. منتور هم بر همین اساس
            راهنمایی می‌کند، نه بر اساس حدس.
          </Script>
        </div>
        <div>
          <p className="font-bold text-gray-900">«یک هفته را جا انداختم، مشکلی پیش می‌آید؟»</p>
          <Script>
            نه، نمودار شما ادامه پیدا می‌کند و فقط آن هفته نقطه ندارد. ولی هرچه ثبت‌ها کامل‌تر
            باشد، تحلیل منتور دقیق‌تر است.
          </Script>
        </div>
        <div>
          <p className="font-bold text-gray-900">«اعدادم را دیگران می‌بینند؟»</p>
          <Script>
            نه. اعداد شما را فقط منتور گروه خودتان و مدیریت دوره می‌بینند. هم‌گروهی‌های شما
            به اعداد شما دسترسی ندارند.
          </Script>
          <p className="mt-1 text-xs text-gray-500">
            دقیق: منتور فقط شرکت‌کنندگان گروه خودش را می‌بیند و این محدودیت روی سرور اعمال
            می‌شود.
          </p>
        </div>
        <div>
          <p className="font-bold text-gray-900">«پاسخم را اشتباه فرستادم»</p>
          <Script>
            اگر تسک اجازهٔ ارسال مجدد داشته باشد، دوباره بفرستید؛ نسخهٔ جدید ثبت می‌شود و
            همان ملاک قرار می‌گیرد. اگر گزینه‌اش را نمی‌بینید به من بگویید تا برایتان باز
            کنم.
          </Script>
        </div>
        <div>
          <p className="font-bold text-gray-900">«چرا وضعیتم قرمز شده؟»</p>
          <Script>
            وضعیت رنگی بر اساس تسک‌های عقب‌افتاده و مدت بی‌فعالیتی خودکار محاسبه می‌شود. با
            رساندن تسک‌های عقب‌افتاده، خودش به سبز برمی‌گردد.
          </Script>
        </div>
      </div>
    </Card>

    {/* ── عیب‌یابی ───────────────────────────────────────────────────────── */}
    <Card
      icon={<Wrench className="h-4 w-4" />}
      title="۷. عیب‌یابی"
      subtitle="وقتی چیزی آن‌طور که انتظار دارید کار نمی‌کند"
    >
      <div className="space-y-3">
        {[
          {
            p: 'نمودار خالی است',
            c: 'یا هیچ فیلدی به شاخص وصل نشده، یا هنوز پاسخی ارسال نشده. تب «تسک‌ها» را باز کنید و ببینید فیلد عددی شاخص دارد یا نه.',
          },
          {
            p: 'نمودار فقط یک نقطه دارد',
            c: 'روند به چند تسک نیاز دارد. تا وقتی فقط یک تسک با آن شاخص ساخته‌اید، یک نقطه بیشتر نخواهید داشت.',
          },
          {
            p: 'شاخص در فهرست انتخاب نیست',
            c: 'یا غیرفعالش کرده‌اید (در پنل شاخص‌ها تیک «فعال» را بزنید)، یا نوع فیلد عددی نیست. فقط «پاسخ عددی» و «طیف امتیازدهی» شاخص می‌پذیرند.',
          },
          {
            p: 'عدد نمودار با چیزی که انتظار دارم فرق می‌کند',
            c: 'سه چیز را چک کنید: کلید «فقط تأییدشده‌ها»، فیلتر گروه، و اینکه میانگین انتخاب شده یا مجموع.',
          },
          {
            p: 'شاخص را حذف کردم، پاسخ‌ها هم پاک شدند؟',
            c: 'نه. حذف شاخص فقط اتصال را قطع می‌کند و عددهای ثبت‌شده سر جایشان می‌مانند. اگر دوباره شاخص بسازید و وصل کنید، نمودار برمی‌گردد.',
          },
          {
            p: 'تسکی را نمی‌توانم حذف کنم',
            c: 'روی تسکی که پاسخ ثبت شده اجازهٔ حذف نیست تا داده از بین نرود. به‌جایش از حالت انتشار خارجش کنید.',
          },
          {
            p: 'فیلدی را نمی‌توانم حذف کنم',
            c: 'اگر آن فیلد پاسخ ثبت‌شده دارد حذف نمی‌شود، چون پاسخ‌ها هم پاک می‌شدند. عنوانش را ویرایش کنید.',
          },
        ].map((row) => (
          <div key={row.p} className="rounded-lg border border-gray-200 p-3">
            <p className="flex items-start gap-1.5 font-bold text-gray-900">
              <AlertTriangle className="mt-1 h-3.5 w-3.5 shrink-0 text-amber-500" />
              {row.p}
            </p>
            <p className="mt-1 pr-5 text-[13px] leading-7 text-gray-600">{row.c}</p>
          </div>
        ))}
      </div>
    </Card>

    {/* ── روال هفتگی ─────────────────────────────────────────────────────── */}
    <Card
      icon={<CheckCircle2 className="h-4 w-4" />}
      title="۸. روال پیشنهادی هفتگی"
      subtitle="چک‌لیست اپراتور"
    >
      <div className="space-y-2">
        {[
          'تسک هفتهٔ جاری را بسازید و فیلد عددی‌اش را به همان شاخص همیشگی وصل کنید.',
          'تسک را منتشر و به گروه‌های مربوطه تخصیص دهید.',
          'در تب «نمای کلی» تعداد عقب‌افتاده‌ها و منتظر بازبینی را ببینید.',
          'به منتورهایی که صف بازبینی‌شان طولانی شده یادآوری کنید.',
          'در تب «تحلیل» ستون «تعداد ثبت» را نگاه کنید و با کسانی که چند هفته نفرستاده‌اند تماس بگیرید.',
          'شرکت‌کنندگان قرمز را در تب «گروه‌ها و افراد» بررسی کنید.',
          'در صورت نیاز، خروجی CSV بگیرید و برای مدرس بفرستید.',
        ].map((t, i) => (
          <div key={i} className="flex gap-2">
            <CheckCircle2 className="mt-1 h-3.5 w-3.5 shrink-0 text-emerald-500" />
            <p className="text-[13px] leading-7 text-gray-700">{t}</p>
          </div>
        ))}
      </div>
    </Card>
  </div>
);

export default PortalGuideTab;
